﻿import { Directive } from "@app/components/directive";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IRootScope } from "@models";
import { Utils } from "@tools";
import * as angular from "angular";

@NgComponent({
    token: Token.KbInfiniteScroll,
    dependencies: [
        Token.$RootScope,
        Token.$Window,
        Token.$Timeout,
        Token.$Compile,
        Token.PromiseTracker,
        Token.$Q,
    ]
})
export class KbInfiniteScroll extends Directive {
    constructor(
        private $rootScope: IRootScope,
        $window: ng.IWindowService,
        $timeout: ng.ITimeoutService,
        $compile: ng.ICompileService,
        promiseTracker: ng.IPromiseTracker,
        $q: ng.IQService
    ) {
        super();

        this.restrict = "A";
        this.replace = false;
        this.scope = false;

        this.link = (scope: any, element, atts: any) => {
            // var checkWhenEnabled: boolean;
            let scrollDistance: number;
            // var scrollEnabled: boolean;

            let window = angular.element($window);

            let $parent: angular.IAugmentedJQuery;
            let $child: angular.IAugmentedJQuery;
            if (angular.isDefined(atts.kbInfiniteScrollParent)) {
                $parent = angular.element(atts.kbInfiniteScrollParent);
                //if we are inside a directive, then the element might not be in the DOM yet,
                //so we search for it in the directive element chain
                if (!$parent.length) {
                    $parent = this.findDirectiveElement(element, atts.kbInfiniteScrollParent);
                }
            } else {
                $parent = $window as any;
            }
            if (angular.isDefined(atts.kbInfiniteScrollChild)) {
                $child = angular.element(atts.kbInfiniteScrollChild);
                //if we are inside a directive, then the element might not be in the DOM yet,
                //so we search for it in the directive element chain
                if (!$child.length) {
                    $child = this.findDirectiveElement(element, atts.kbInfiniteScrollChild);
                }
            } else {
                $child = element;
            }

            let isHorizontal: boolean = false;
            if (angular.isDefined(atts.kbInfiniteScrollOrientation)) {
                if (atts.kbInfiniteScrollOrientation.toLowerCase() == "horizontal") {
                    isHorizontal = true;
                }
            }

            let hasReverseScroll: boolean = false;
            if (angular.isDefined(atts.kbInfiniteScrollReverse)) {
                hasReverseScroll = true;
            }

            let template;
            if (isHorizontal) {
                template = `
<div ng-class="{'is-hidden': !infiniteScrollTracker.active()}"
     style="width: 50px; 
            height: 100%; 
            display: inline-flex; 
            display: -webkit-inline-flex; 
            align-items: center; 
            justify-content: center;">
</div>`;
            } else {
                template = `
<div ng-class="{'is-hidden': !infiniteScrollTracker.active()}"
     style="height: 50px; 
            display: flex; 
            display: -webkit-flex; 
            align-items: center; 
            justify-content: center;">
</div>`;
            }
            let $template = $(template);
            let spinner = `<img ng-src="{{::'images/spinning_circle.gif' | asset}}" style="width: 28px; height: 28px;">`;

            $template.append(spinner);
            $template = $compile($template)(scope);
            element.append($template);

            scrollDistance = 0;
            if (atts.kbInfiniteScrollDistance) {
                scope.$watch(atts.kbInfiniteScrollDistance, value => {
                    return scrollDistance = parseFloat(value);
                });
            }

            let busy = false;
            let handler = Utils.debounce(() => {
                if (busy) return;
                busy = true;

                let shouldScroll = false;

                if (isHorizontal) {
                    let parentWidth = $parent[0].clientWidth;
                    let portalRight = parentWidth + $parent[0].scrollLeft;
                    let remaining = $child[0].clientWidth - portalRight;
                    shouldScroll = remaining <= parentWidth * scrollDistance;
                } else {
                    let parentHeight = $parent[0].clientHeight;
                    let portalBottom = parentHeight + $parent[0].scrollTop;
                    let remaining = $child[0].clientHeight - portalBottom;
                    shouldScroll = remaining <= parentHeight * scrollDistance;
                }

                return $q.when(shouldScroll && scope.$eval(atts.kbInfiniteScroll)).then(() => {
                    busy = false;
                });
            }, 10);

            let reverseBusy = false;
            let reverseHandler = Utils.debounce(() => {
                if (reverseBusy || scope.$eval(atts.kbInfiniteScrollReverseDisabled)) return;
                reverseBusy = true;

                let shouldScroll = false;

                if (isHorizontal) {
                    let threshold = $parent[0].clientWidth * scrollDistance;
                    let curScroll = $parent[0].scrollLeft;

                    if (curScroll <= threshold) {
                        $parent[0].scrollLeft = threshold;
                    }

                    shouldScroll = curScroll <= threshold;
                } else {
                    let threshold = $parent[0].clientHeight * scrollDistance;
                    let curScroll = $parent[0].scrollTop;

                    // This is needed because if the scrollbar reaches the top 
                    // it will not move down once the items are added to the top of the list
                    if (curScroll <= threshold) {
                        $parent[0].scrollTop = threshold;
                    }

                    shouldScroll = curScroll <= threshold;
                }

                return $q.when(shouldScroll && scope.$eval(atts.kbInfiniteScrollReverse)).then(() => {
                    reverseBusy = false;
                });
            }, 10);

            let onMousewheel = (e: WheelEvent) => {
                if (e.deltaY === 1) {
                    e.preventDefault();
                }
            };

            $parent[0].addEventListener("scroll", handler);
            $parent[0].addEventListener("mousewheel", onMousewheel) //need the mousewheel event for a bug in chrome:  https://stackoverflow.com/a/47684257/709968

            if (hasReverseScroll) {
                $parent[0].addEventListener("scroll", reverseHandler);
            }

            //$parent.on("scroll", handler);
            scope.$on("$destroy", () => {
                $parent[0].removeEventListener("scroll", handler);
                $parent[0].removeEventListener("scroll", reverseHandler);
                $parent[0].removeEventListener("mousewheel", onMousewheel);
                //return $parent.off("scroll", handler);
            });
            return $timeout((() => {
                if (atts.kbInfiniteScrollImmediateCheck) {
                    if (scope.$eval(atts.kbInfiniteScrollImmediateCheck)) {
                        return handler();
                    }
                } else {
                    return handler();
                }
            }), 0);

        };

    }

    public findDirectiveElement(directiveElement: ng.IAugmentedJQuery, selector: string) {
        //if we are inside a directive, then the element might not be in the DOM yet,
        //so we search for it in the directive element chain
        let p = directiveElement;
        while (p.parent().length) {
            p = p.parent();
        }
        return p.find(selector).addBack(selector);
    }
}
